import { useMemo, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { Badge } from '@/components/ui/Badge';

function InviteRow() {
  const { inviteTech, userRole, cloudMode } = useApp();
  const [email, setEmail] = useState('');
  const [role,  setRole]  = useState('tech');
  const [sending, setSending] = useState(false);

  if (!cloudMode) return null;
  const canInvite = userRole === 'owner' || userRole === 'admin';

  const handleSend = async () => {
    if (!email.includes('@')) return;
    setSending(true);
    try {
      const res = await inviteTech({ email: email.trim(), role });
      if (res.ok) setEmail('');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 12, borderLeft: '3px solid var(--cyan)' }}>
      <div className="card-head">
        <div>
          <span className="card-title">✉ Invite a teammate by email</span>
          <div style={{ fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>
            They'll receive a magic link to sign up and auto-join your company.
          </div>
        </div>
      </div>
      <div className="card-body" style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="teammate@example.com" disabled={!canInvite} />
        </div>
        <div className="field" style={{ width: 130, marginBottom: 0 }}>
          <label>Role</label>
          <select value={role} onChange={e => setRole(e.target.value)} disabled={!canInvite} style={{ width: '100%', padding: '6px 8px', background: 'var(--s2)', color: 'var(--t1)', border: '1px solid var(--b2)', borderRadius: 4 }}>
            <option value="tech">Tech</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button className="btn btn-primary btn-sm" disabled={!canInvite || sending || !email.includes('@')} onClick={handleSend}>
          {sending ? 'Sending…' : 'Send invite'}
        </button>
      </div>
      {!canInvite && (
        <div style={{ padding: '0 15px 12px', fontSize: 11.5, color: 'var(--amber)' }}>
          Only owners and admins can invite teammates.
        </div>
      )}
    </div>
  );
}

const COURSES = [
  { name: 'CPO Certification',         allRequired: true  },
  { name: 'Chemical Safety & LSI',     allRequired: true  },
  { name: 'Equipment Diagnostics',     allRequired: false },
  { name: 'Customer Service & Upsell', allRequired: false },
];

function certBadge(s) {
  if (s === 'done')        return <Badge color="var(--teal)"  bg="var(--teal-t)">Complete</Badge>;
  if (s === 'in_progress') return <Badge color="var(--amber)" bg="var(--amber-t)">In Progress</Badge>;
  return                          <Badge color="var(--t3)"    bg="var(--s3)">Not Started</Badge>;
}

function initialsFor(name) {
  return (name || '').split(/\s+/).filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '??';
}

export default function Team() {
  const { team, customers, stops, openModal } = useApp();

  // Compute live per-tech stats from real customer/stop data
  const techStats = useMemo(() => {
    const stats = {};
    team.forEach(t => {
      const myCusts = customers.filter(c => c.tech === t.name);
      const myStops = stops.filter(s => s.tech === t.name);
      const myDone  = myStops.filter(s => s.status === 'done');
      const photoStops = myDone.filter(s => Array.isArray(s.photos) && s.photos.length > 0);
      stats[t.id] = {
        pools:     myCusts.length,
        rating:    t.rating || 5.0,
        photoRate: myDone.length ? Math.round((photoStops.length / myDone.length) * 100) : 100,
        done:      myStops.length ? Math.round((myDone.length / myStops.length) * 100) : 100,
      };
    });
    return stats;
  }, [team, customers, stops]);

  const handleAdd = () => openModal('technician', {});
  const handleEdit = (tech) => openModal('technician', { tech });

  return (
    <div className="page fade-up">
      <div className="ph">
        <div>
          <div className="ptitle">Team Management</div>
          <div className="psub">
            {team.length === 0
              ? 'No technicians yet — add your first one'
              : `${team.filter(t => t.active !== false).length} active technician${team.length === 1 ? '' : 's'}`
            }
          </div>
        </div>
        <div className="pacts">
          <button className="btn btn-primary btn-sm" onClick={handleAdd}>+ Add Tech</button>
        </div>
      </div>

      <InviteRow />

      {team.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 14, color: 'var(--t2)', marginBottom: 12 }}>
              No technicians on your team yet. Add one to start assigning customers and stops.
            </div>
            <button className="btn btn-primary" onClick={handleAdd}>+ Add Your First Technician</button>
          </div>
        </div>
      ) : (
        <div className="g3">
          {team.map(t => {
            const s = techStats[t.id] || { pools: 0, rating: 5.0, photoRate: 100, done: 100 };
            const initials = t.initials || initialsFor(t.name);
            return (
              <div
                key={t.id}
                className="card"
                onClick={() => handleEdit(t)}
                style={{ cursor: 'pointer' }}
                title="Click to edit"
              >
                <div style={{ height: 3, background: `linear-gradient(90deg,${t.color},transparent)` }} />
                <div className="card-head">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      className="tech-av"
                      style={{ background: `linear-gradient(135deg,${t.color},var(--teal))` }}
                    >
                      {initials}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--t1)' }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--t3)' }}>
                        {t.role} {t.since ? `· Since ${t.since}` : ''}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: 14 }}>{t.active === false ? '⚪' : '🟢'}</span>
                </div>
                <div className="card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                    {[
                      ['Pools',  s.pools,         'var(--t1)'],
                      ['Rating', `${s.rating}★`,  'var(--cyan)'],
                      ['Done',   `${s.done}%`,    'var(--teal)'],
                    ].map(([l, v, c]) => (
                      <div key={l} style={{
                        background: 'var(--s2)',
                        borderRadius: 8,
                        padding: 10,
                        textAlign: 'center',
                        border: '1px solid var(--b1)',
                      }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: c }}>{v}</div>
                        <div style={{ fontSize: 9.5, color: 'var(--t3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{l}</div>
                      </div>
                    ))}
                  </div>

                  <div className="prog-row">
                    <div className="prog-label">Photo Rate</div>
                    <div className="prog-track">
                      <div className="prog-fill" style={{ width: `${s.photoRate}%`, background: 'var(--teal)' }} />
                    </div>
                    <div className="prog-val" style={{ color: 'var(--teal)' }}>{s.photoRate}%</div>
                  </div>
                  <div className="prog-row">
                    <div className="prog-label">Completion</div>
                    <div className="prog-track">
                      <div className="prog-fill" style={{ width: `${s.done}%`, background: 'var(--cyan)' }} />
                    </div>
                    <div className="prog-val" style={{ color: 'var(--cyan)' }}>{s.done}%</div>
                  </div>

                  <div style={{ height: 1, background: 'var(--b1)', margin: '10px 0' }} />
                  <div style={{ fontSize: 12, color: 'var(--t3)' }}>
                    {t.phone || '—'}
                    {t.email && (
                      <>
                        {' · '}
                        <a href={`mailto:${t.email}`} style={{ color: 'var(--t3)' }} onClick={(e) => e.stopPropagation()}>
                          {t.email}
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
